import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useManualAssets, type ManualAssetInput } from '@/providers/manual/hooks/useManualAssets';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ASSET_CLASS_ICONS } from '@/constants/investmentIcons';
import { getCurrencyLabel } from '@/lib/currency';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

export type ManualAssetFormMode = 'create' | 'edit';

interface ManualAssetFormProps {
  mode: ManualAssetFormMode;
  initialAsset?: any | null;
  onSubmitSuccess?: () => void;
  onCancel?: () => void;
  variant?: 'embedded' | 'dialog';
  onDeleted?: () => void;
  suppressToasts?: boolean;
}

// Active currencies from DB with a minimal fallback

const ASSET_TYPES: Array<{ value: ManualAssetInput['asset_type']; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'equity', label: 'Equity' },
  { value: 'fixed_income', label: 'Fixed Income' },
  { value: 'alternative', label: 'Alternative' },
  { value: 'multi_asset', label: 'Multi-Asset' },
];

export function ManualAssetForm({ mode, initialAsset = null, onSubmitSuccess, onCancel, variant = 'embedded', onDeleted, suppressToasts = false }: ManualAssetFormProps) {
  const { createManualAsset, updateManualAsset, deleteManualAsset, checkPortfolioAllocations } = useManualAssets();
  const isEdit = mode === 'edit';

  // Active currencies from DB (fallback to a small set if none returned)
  const { data: activeCurrencies } = useQuery({
    queryKey: ["currencies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("currencies")
        .select("code,symbol,is_active")
        .eq("is_active", true)
        .order("code", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const [assetType, setAssetType] = useState<ManualAssetInput['asset_type']>(
    (initialAsset?.asset_type as ManualAssetInput['asset_type']) || 'alternative'
  );

  const [formData, setFormData] = useState<Omit<ManualAssetInput, 'asset_type'>>(() => {
    if (isEdit && initialAsset) {
      return {
        asset_name: initialAsset.asset_name || '',
        symbol: initialAsset.symbol || '',
        quantity: initialAsset.quantity ?? undefined,
        market_value: initialAsset.market_value ?? 0,
        book_value: initialAsset.book_value ?? undefined,
        currency: (initialAsset.currency || 'CAD').toUpperCase(),
        registered: initialAsset.registered || 'non-registered',
        description: initialAsset.description || '',
      };
    }
    return {
      asset_name: '',
      symbol: '',
      quantity: undefined,
      market_value: 0,
      book_value: undefined,
      currency: 'CAD',
      registered: 'non-registered',
      description: '',
    };
  });

  const currencyOptions = useMemo(() => {
    if (activeCurrencies && activeCurrencies.length > 0) {
      const list = activeCurrencies.map((c: any) => {
        const code = String(c.code || '').toUpperCase();
        const label = getCurrencyLabel(code);
        return { code, label };
      });
      const preferred = ["CAD", "USD"];
      const preferredItems = preferred
        .map((p) => list.find((c) => c.code === p))
        .filter(Boolean) as Array<{ code: string; label: string }>;
      const rest = list.filter((c) => !preferred.includes(c.code));
      return [...preferredItems, ...rest];
    }
    return ["CAD", "USD", "EUR", "GBP"].map(code => ({ code, label: getCurrencyLabel(code) }));
  }, [activeCurrencies]);

  const idLabel = useMemo(() => {
    switch (assetType) {
      case 'equity': return 'Symbol/Ticker';
      case 'fixed_income': return 'Code/Identifier';
      case 'alternative': return 'Code/Identifier';
      case 'multi_asset': return 'Code/Identifier';
      case 'cash':
      default: return 'Institution/Provider';
    }
  }, [assetType]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCurrencyChange = (value: string) => {
    setFormData(prev => ({ ...prev, currency: value }));
  };

  const handleRegisteredChange = (value: string) => {
    setFormData(prev => ({ ...prev, registered: value as 'registered' | 'non-registered' }));
  };

  const validate = (): string | null => {
    if (!formData.asset_name?.trim()) return 'Asset name is required';
    const mv = Number(formData.market_value);
    if (!mv || mv <= 0) return 'Market value must be greater than zero';
    if (assetType === 'equity' && !formData.symbol?.trim()) return 'Symbol is required for equity assets';
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }

    const input: ManualAssetInput = {
      ...formData,
      asset_type: assetType,
      market_value: Number(formData.market_value),
      book_value: formData.book_value != null && String(formData.book_value).length > 0 ? Number(formData.book_value) : undefined,
      quantity: formData.quantity != null && String(formData.quantity).length > 0 ? Number(formData.quantity) : undefined,
    };

    try {
      if (isEdit && initialAsset?.id) {
        await updateManualAsset(String(initialAsset.id), input);
        if (!suppressToasts) { toast.success('Asset updated'); }
      } else {
        await createManualAsset(input);
        toast.success('Asset created');
      }
      onSubmitSuccess?.();
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCascade, setConfirmCascade] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<string>('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      const id = String(initialAsset?.id || '');
      if (!id) return;
      const { count, portfolioNames } = await checkPortfolioAllocations(id);
      const suffix = portfolioNames?.length ? ` (e.g., ${portfolioNames.slice(0, 3).join(', ')}${portfolioNames.length > 3 ? '…' : ''})` : '';
      if (count > 0) {
        setConfirmCascade(true);
        setConfirmMessage(`This asset is used in ${count} portfolio${count !== 1 ? 's' : ''}${suffix}. Remove from those portfolios and delete?`);
      } else {
        setConfirmCascade(false);
        setConfirmMessage('Delete this asset? This action cannot be undone.');
      }
      setPendingDeleteId(id);
      setConfirmOpen(true);
    } catch (e: any) {
      toast.error(e?.message || 'Unable to check portfolio references');
    }
  };

  const confirmDelete = async () => {
    const id = pendingDeleteId;
    if (!id) { setConfirmOpen(false); return; }
    try {
      if (confirmCascade) {
        await deleteManualAsset(id, true);
      } else {
        await deleteManualAsset(id);
      }
      toast.success('Asset deleted');
      setConfirmOpen(false);
      onDeleted?.();
    } catch (e: any) {
      const msg = e?.message || 'Delete failed';
      toast.error(msg.startsWith('PORTFOLIO_ALLOCATIONS_EXIST:') ? 'This asset is referenced in portfolios. Please try again.' : msg);
      setConfirmOpen(false);
    }
  };

  return (
    <div className={variant === 'embedded' ? 'space-y-4' : ''}>
      {/* Asset Name */}
      <div className="space-y-2">
        <Label htmlFor="assetName">Asset Name *</Label>
        <Input id="assetName" name="asset_name" value={formData.asset_name} onChange={handleInputChange} placeholder="Enter asset name" />
      </div>

      {/* Asset Type + Registration */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label id="assetTypeLabel" className="text-sm font-medium">Asset Type *</Label>
          <Select value={assetType} onValueChange={(v) => setAssetType(v as ManualAssetInput['asset_type'])}>
            <SelectTrigger aria-labelledby="assetTypeLabel">
              <SelectValue placeholder="Select asset type" />
            </SelectTrigger>
            <SelectContent>
              {ASSET_TYPES.map(t => {
                const Icon = ASSET_CLASS_ICONS[t.value] || ASSET_CLASS_ICONS['unclassified'];
                return (
                  <SelectItem key={t.value} value={t.value}>
                    <span className="inline-flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      {t.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label id="registeredLabel">Registration Status</Label>
          <Select value={formData.registered} onValueChange={handleRegisteredChange}>
            <SelectTrigger aria-labelledby="registeredLabel">
              <SelectValue placeholder="Select registration status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="non-registered">Non-Registered</SelectItem>
              <SelectItem value="registered">Registered</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Currency & Market Value */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label id="currencyLabel">Currency *</Label>
          <Select value={formData.currency} onValueChange={handleCurrencyChange}>
            <SelectTrigger aria-labelledby="currencyLabel">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {currencyOptions.map(c => (
                <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="marketValue">Market Value *</Label>
          <Input id="marketValue" name="market_value" type="number" min="0" step="0.01" value={formData.market_value as any} onChange={handleInputChange} placeholder="0.00" />
        </div>
      </div>

      {/* Quantity & Book Value */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="quantity">Quantity/Units</Label>
          <Input id="quantity" name="quantity" type="number" min="0" step="0.01" value={(formData.quantity as any) ?? ''} onChange={handleInputChange} placeholder="0" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bookValue">Book Value</Label>
          <Input id="bookValue" name="book_value" type="number" min="0" step="0.01" value={(formData.book_value as any) ?? ''} onChange={handleInputChange} placeholder="0.00" />
        </div>
      </div>

      {/* Identifier / Description / Registered */}
      <div className="space-y-2">
        <Label htmlFor="symbol">{idLabel}</Label>
        <Input id="symbol" name="symbol" value={formData.symbol || ''} onChange={handleInputChange} placeholder="e.g., AAPL, Property Address, Fund Name" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea id="description" name="description" value={formData.description || ''} onChange={handleInputChange} placeholder="Optional description of the asset" rows={3} />
      </div>

      

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        )}
        {isEdit && initialAsset?.id && (
          <Button type="button" variant="destructive" onClick={handleDelete}>Delete</Button>
        )}
        <Button type="button" onClick={onSubmit}>{isEdit ? 'Save Changes' : 'Create Asset'}</Button>
      </div>

      {/* Confirm delete dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
